import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider } from "antd";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import { useAuthStore } from "./store/useAuthStore";
import { authApi } from "./api/auth";
import SiteLoader from "./views/components/SiteLoader";
import Login from "./views/pages/auth/Login";
import AppLayout from "./views/components/Layout";
import Dashboard from "./views/pages/dashboard/Dashboard";
import Companies from "./views/pages/companies/Companies";
import Plans from "./views/pages/plans/Plans";
import Industries from "./views/pages/industries/Industries";
import Roles from "./views/pages/roles/Roles";
import Admins from "./views/pages/admins/Admins";
import ActivityLog from "./views/pages/activityLog/ActivityLog";
import CustomFields from "./views/pages/customFields/CustomFields";
import FeatureFlags from "./views/pages/featureFlags/FeatureFlags";
import AppVersions from "./views/pages/appVersions/AppVersions";
import BackgroundJobs from "./views/pages/backgroundJobs/BackgroundJobs";
import DataExports from "./views/pages/dataExports/DataExports";
import SyncQueue from "./views/pages/syncQueue/SyncQueue";
import RateLimitLogs from "./views/pages/rateLimitLogs/RateLimitLogs";
import CompanyConfig from "./views/pages/companyConfig/CompanyConfig";
import Certifications from "./views/pages/certifications/Certifications";
import PushTokens from "./views/pages/pushTokens/PushTokens";
import InventoryPage from "./views/pages/inventory/InventoryPage";
import FleetPage from "./views/pages/fleet/FleetPage";
import JobConfigPage from "./views/pages/jobConfig/JobConfigPage";
import JobsPage from "./views/pages/jobs/JobsPage";
import InvoicesPage from "./views/pages/invoices/InvoicesPage";
import QuotationsPage from "./views/pages/quotations/QuotationsPage";
import PurchaseOrdersPage from "./views/pages/purchaseOrders/PurchaseOrdersPage";
import CompliancePage from "./views/pages/compliance/CompliancePage";
import HRPage from "./views/pages/hr/HRPage";
import DocumentsPage from "./views/pages/documents/DocumentsPage";
import IntegrationsPage from "./views/pages/integrations/IntegrationsPage";
import PQQPage from "./views/pages/pqq/PQQPage";
import BillingPage from "./views/pages/billing/BillingPage";
import AnnouncementsPage from "./views/pages/announcements/AnnouncementsPage";
import ClientsPage from "./views/pages/clients/ClientsPage";
import EmployeeLocationsPage from "./views/pages/employeeLocations/EmployeeLocationsPage";
import PlatformFleetPage from "./views/pages/platformFleet/PlatformFleetPage";
import PlatformCompliancePage from "./views/pages/platformCompliance/PlatformCompliancePage";
import OnboardingOverviewPage from "./views/pages/onboardingOverview/OnboardingOverviewPage";
import PlatformPipelinePage from "./views/pages/platformPipeline/PlatformPipelinePage";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

function App() {
  const { token, admin, setAuth, clearAuth } = useAuthStore();
  const [loading, setLoading] = useState(!!token);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    authApi
      .me()
      .then((res) => {
        setAuth(res.data.data, token);
      })
      .catch(() => {
        clearAuth();
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SiteLoader />;

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#4c1d95",
            borderRadius: 8,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          },
        }}
      >
        <BrowserRouter>
          {!admin ? (
            <Login />
          ) : (
            <Routes>
              <Route element={<AppLayout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/companies" element={<Companies />} />
                <Route path="/plans" element={<Plans />} />
                <Route path="/industries" element={<Industries />} />
                <Route path="/roles" element={<Roles />} />
                <Route path="/admins" element={<Admins />} />
                <Route path="/activity" element={<ActivityLog />} />
                <Route path="/custom-fields" element={<CustomFields />} />
                <Route path="/feature-flags" element={<FeatureFlags />} />
                <Route path="/app-versions" element={<AppVersions />} />
                <Route path="/background-jobs" element={<BackgroundJobs />} />
                <Route path="/data-exports" element={<DataExports />} />
                <Route path="/sync-queue" element={<SyncQueue />} />
                <Route path="/rate-limit-logs" element={<RateLimitLogs />} />
                <Route path="/company-config/:companyId" element={<CompanyConfig />} />
                <Route path="/certifications" element={<Certifications />} />
                <Route path="/push-tokens" element={<PushTokens />} />
                <Route path="/inventory/:companyId" element={<InventoryPage />} />
                <Route path="/fleet/:companyId" element={<FleetPage />} />
                <Route path="/job-config/:companyId" element={<JobConfigPage />} />
                <Route path="/company-fields/:companyId" element={<CustomFields />} />
                <Route path="/jobs/:companyId" element={<JobsPage />} />
                <Route path="/invoices/:companyId" element={<InvoicesPage />} />
                <Route path="/quotations/:companyId" element={<QuotationsPage />} />
                <Route path="/purchase-orders/:companyId" element={<PurchaseOrdersPage />} />
                <Route path="/compliance/:companyId" element={<CompliancePage />} />
                <Route path="/hr/:companyId" element={<HRPage />} />
                <Route path="/documents/:companyId" element={<DocumentsPage />} />
                <Route path="/integrations/:companyId" element={<IntegrationsPage />} />
                <Route path="/pqq/:companyId" element={<PQQPage />} />
                <Route path="/billing/:companyId" element={<BillingPage />} />
                <Route path="/clients/:companyId" element={<ClientsPage />} />
                <Route path="/employee-locations/:companyId" element={<EmployeeLocationsPage />} />
                <Route path="/announcements" element={<AnnouncementsPage />} />
                <Route path="/platform-fleet" element={<PlatformFleetPage />} />
                <Route path="/platform-compliance" element={<PlatformCompliancePage />} />
                <Route path="/onboarding-overview" element={<OnboardingOverviewPage />} />
                <Route path="/pipeline-overview" element={<PlatformPipelinePage />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Routes>
          )}
        </BrowserRouter>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
